import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';

/**
 * LocalisationCombobox — Searchable dropdown (combobox).
 * Works reliably on iOS/Android (unlike native <datalist>).
 * Props:
 *   - value: string
 *   - onChange: (string) => void
 *   - options: string[]
 *   - placeholder: string
 *   - testId: string
 */
const LocalisationCombobox = ({ value = '', onChange, options = [], placeholder = '', testId = 'loc-combo' }) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const containerRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, []);

  const filtered = (options || []).filter(o =>
    !filter.trim() || o.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center w-full h-9 border border-blue-200 rounded bg-white focus-within:ring-2 focus-within:ring-blue-400">
        <input
          type="text"
          value={open ? filter : (value || '')}
          onChange={(e) => {
            const v = e.target.value;
            if (open) setFilter(v);
            else { onChange(v); }
          }}
          onFocus={() => { setOpen(true); setFilter(value || ''); }}
          placeholder={placeholder}
          className="flex-1 h-full px-2 text-sm bg-transparent focus:outline-none"
          data-testid={testId}
        />
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            if (!open) { setFilter(value || ''); setOpen(true); }
            else setOpen(false);
          }}
          className="h-full px-2 flex items-center text-blue-600 hover:bg-blue-50"
          data-testid={`${testId}-toggle`}
          aria-label="Ouvrir la liste"
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      
      {open && (
        <div className="absolute left-0 right-0 z-50 mt-1 bg-white border border-blue-200 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid={`${testId}-list`}>
          {filter.trim() && !options.some(o => o.toLowerCase() === filter.toLowerCase()) && (
            <button
              type="button"
              onClick={() => { onChange(filter); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm text-blue-700 hover:bg-blue-50 border-b border-blue-100 flex items-center gap-2"
            >
              <Check className="w-3.5 h-3.5" />
              Utiliser : <span className="font-medium">« {filter} »</span>
            </button>
          )}
          {filtered.length === 0 && !filter.trim() && (
            <div className="px-3 py-2 text-sm text-slate-400 italic">Aucune option</div>
          )}
          {filtered.map((opt, i) => (
            <button
              key={`${opt}-${i}`}
              type="button"
              onClick={() => { onChange(opt); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${opt === value ? 'bg-blue-50 font-medium text-blue-800' : 'text-slate-700'}`}
              data-testid={`${testId}-option-${i}`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocalisationCombobox;
